import { useUser } from "@clerk/clerk-react";
import { toNameCase } from "../../../lib/fieldFormat";
import { useCurrentUser } from "./useCurrentUser";

export interface SignedInUser {
  /** The user's own name, or their email if Clerk holds no name. `""` until
   *  Clerk has loaded, and for the account that somehow has neither. */
  fullName: string;
  /** What to greet them by - the first word of their name, or the part of
   *  their email before the `@`, written as "Sameer" however it was typed.
   *  Empty on the same terms as `fullName`. */
  firstName: string;
  /** The given name the account was opened under, for a form that collects one
   *  - `""` where Clerk holds no name at all. Unlike `firstName` it never falls
   *  back to the email address: a greeting can address "Sameer" from
   *  `sameer@…`, but a First name field would then be storing half an address
   *  as somebody's name. */
  givenName: string;
  /** The family name, on the same terms as `givenName` - and `""` for an
   *  account known by one word only, which is a name with no surname in it
   *  rather than one waiting to be split. */
  familyName: string;
  email: string;
  /** Their profile picture - the one saved on the Settings form, or the one
   *  the account was opened with - and `""` for an account with none. Clerk
   *  hands out a generated initials avatar otherwise, and the rail draws its
   *  own. */
  imageUrl: string;
  /** False while Clerk is still restoring the session, when every field is
   *  empty because nothing is known yet rather than because nothing is set. */
  isLoaded: boolean;
}

/**
 * The signed-in user, as the screens want to address them.
 *
 * Two sources, in that order of trust:
 *
 *  - our own backend, through `useCurrentUser` — `GET /api/v1/me/`. This is
 *    the record the Settings form edits, so it is what the person actually
 *    chose to be called and the picture they actually chose. It wins wherever
 *    it holds a value, which is what makes a name changed on the General tab
 *    show up in the rail rather than only after the next sign-in.
 *  - Clerk, which owns the identity and is already in memory, so it costs no
 *    request. It answers for the frame before the read lands, for a screen
 *    outside the provider, and for an account whose profile is still empty.
 *
 * Within Clerk, which field carries the name depends on how the account was
 * made, hence the walk down:
 *
 *  - `fullName` is set when the Clerk instance collects names, and by Google.
 *  - `unsafeMetadata.fullName` is where our own sign-up screen puts it - the
 *    one field a Clerk instance accepts regardless of what it collects.
 *  - the email address is the last thing we know them by. Better a real
 *    address than a name belonging to somebody else.
 *
 * Nothing here falls back to mock data: a greeting has to be the person
 * reading it, and a stand-in name is worse than no name at all. Call sites
 * decide what an empty string reads as.
 */
export function useSignedInUser(): SignedInUser {
  const { user, isLoaded } = useUser();
  // `null` until the read lands, and outside the provider - a page rendered on
  // its own in a test, or an auth screen with no session yet. Every field below
  // reads it first and falls through to Clerk, so an absent one changes
  // nothing about what is shown.
  const { user: account } = useCurrentUser();

  const email = account?.email || user?.primaryEmailAddress?.emailAddress || "";

  // The two halves ahead of the whole, because the two halves are what the
  // Settings form writes: a rename saved there is `first_name` and `last_name`,
  // and reading the composed `full_name` first would show the old name for as
  // long as the backend took to recompose it.
  const savedName =
    [account?.firstName, account?.lastName].filter(Boolean).join(" ") ||
    account?.fullName ||
    "";

  const name =
    savedName ||
    user?.fullName?.trim() ||
    (user?.unsafeMetadata?.fullName as string | undefined)?.trim() ||
    "";

  // Name-cased on the way out, so "SAMEER" and "sameer" both read "Sameer" in
  // a greeting. Sign-up takes the name as a single free-text field and nothing
  // between here and Clerk tidies it, so whatever shape it was typed in is the
  // shape it comes back in — and a greeting is the one place a name is spoken
  // rather than quoted, where all-caps reads as shouting at the reader.
  //
  // Only this field. `fullName` is what BookCallPage sends to Cal and what the
  // rail draws, and one of the things it can be is an email address, which
  // name-casing would mangle into "Sameer@Openui.In".
  const firstName = toNameCase(
    account?.firstName ||
      user?.firstName?.trim() ||
      name.split(/\s+/)[0] ||
      // `sameer@…` is still recognisably them; the full address is not a name.
      email.split("@")[0] ||
      ""
  );

  // The name split the way a form asks for it. Clerk's own two fields answer
  // where the instance collects them; where it does not, the free-text name is
  // split on its first space — first word given, whatever follows family, so a
  // double-barrelled surname stays whole. Name-cased for the same reason the
  // form cases what is typed into it: what is stored heads the profile.
  const words = name.split(/\s+/).filter(Boolean);
  const givenName = toNameCase(
    account?.firstName || user?.firstName?.trim() || words[0] || ""
  );
  const familyName = toNameCase(
    account?.lastName || user?.lastName?.trim() || words.slice(1).join(" ")
  );

  return {
    fullName: name || email,
    firstName,
    givenName,
    familyName,
    email,
    // The profile photo the practice keeps, ahead of whatever the account was
    // opened with: it is the one the Settings form changes, and the one the
    // rail should redraw the moment it does.
    imageUrl: account?.photoUrl || (user?.hasImage ? user.imageUrl : ""),
    isLoaded,
  };
}

export default useSignedInUser;
